/** Mask candidate PII for client-facing views (demographics, read-only profile). */

function maskToken(part, { upper = true } = {}) {
  const raw = String(part || '').trim();
  if (!raw) return '';
  const x = upper ? 'X' : 'x';
  if (raw.length <= 1) return x;
  if (raw.length === 2) return `${raw[0]}${x}`;
  return `${raw[0]}${x.repeat(raw.length - 2)}${raw[raw.length - 1]}`;
}

/** e.g. "Name" → "NXXe", "John Doe" → "JXXn DXXe" */
export function maskPersonName(name) {
  const raw = String(name || '').trim();
  if (!raw || raw === '—') return '—';
  return raw
    .split(/\s+/)
    .filter(Boolean)
    .map(part => maskToken(part))
    .join(' ');
}

/** e.g. team@paytiemp.com → tXXX@pXXXXXX.cXX */
export function maskEmail(email) {
  const raw = String(email || '').trim();
  if (!raw || raw === '—') return '—';
  const at = raw.indexOf('@');
  if (at < 1) return maskToken(raw);
  const local = raw.slice(0, at);
  const domain = raw.slice(at + 1);
  const domainParts = domain.split('.');
  const tld = domainParts.length > 1 ? domainParts.pop() : '';
  const host = domainParts.join('.') || domain;
  const maskedHost = host.includes('.')
    ? host
        .split('.')
        .map(p => maskToken(p))
        .join('.')
    : maskToken(host);
  const maskedTld = tld ? maskToken(tld) : '';
  return `${maskToken(local)}@${maskedTld ? `${maskedHost}.${maskedTld}` : maskedHost}`;
}

/** e.g. 7619540472 → 7XXXXXXXX2 */
export function maskPhone(phone) {
  const raw = String(phone || '').trim();
  if (!raw || raw === '—') return '—';
  const digits = raw.replace(/\D/g, '');
  if (digits.length <= 2) return 'X'.repeat(Math.max(2, digits.length));
  if (digits.length <= 4) {
    return `${digits[0]}${'X'.repeat(digits.length - 2)}${digits[digits.length - 1]}`;
  }
  return `${digits[0]}${'X'.repeat(digits.length - 2)}${digits[digits.length - 1]}`;
}

/** e.g. linkedin.com/in/johndoe → linkedin.com/in/jXXXXXXe */
export function maskLinkedIn(url) {
  const raw = String(url || '').trim();
  if (!raw || raw === '—') return '—';
  try {
    const withProto = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    const parsed = new URL(withProto);
    const segments = parsed.pathname.split('/').filter(Boolean);
    if (!segments.length) return maskToken(raw);
    const maskedPath = segments
      .map((seg, idx) => (idx === segments.length - 1 ? maskToken(seg) : seg))
      .join('/');
    return `${parsed.hostname}/${maskedPath}`;
  } catch {
    return maskToken(raw);
  }
}

const PII_COLUMN_KEYS = new Set(['name', 'email', 'phone', 'mobile', 'linkedin', 'candidateName', 'candidateEmail', 'candidatePhone']);

export function isPiiColumnKey(key) {
  return PII_COLUMN_KEYS.has(key);
}

export function maskInsightRowForClient(row) {
  if (!row || typeof row !== 'object') return row;
  const next = { ...row };
  if (next.name != null) next.name = maskPersonName(next.name);
  if (next.email != null) next.email = maskEmail(next.email);
  if (next.phone != null) next.phone = maskPhone(next.phone);
  if (next.mobile != null) next.mobile = maskPhone(next.mobile);
  if (next.linkedin != null) next.linkedin = maskLinkedIn(next.linkedin);
  if (next.candidateName != null) next.candidateName = maskPersonName(next.candidateName);
  if (next.candidateEmail != null) next.candidateEmail = maskEmail(next.candidateEmail);
  if (next.candidatePhone != null) next.candidatePhone = maskPhone(next.candidatePhone);
  return next;
}

export function maskCandidateProfileForClient(data) {
  if (!data) return data;
  return {
    ...data,
    displayName: maskPersonName(
      data.displayName || data.personal?.name || data.contact?.name || ''
    ),
    displayEmail: maskEmail(data.displayEmail || data.contact?.email || ''),
    displayPhone: maskPhone(data.displayPhone || data.contact?.phone || ''),
    contact: {
      ...(data.contact || {}),
      name: maskPersonName(data.contact?.name || data.personal?.name || ''),
      email: maskEmail(data.contact?.email || ''),
      phone: maskPhone(data.contact?.phone || ''),
      linkedin: maskLinkedIn(data.contact?.linkedin || '')
    },
    personal: {
      ...(data.personal || {}),
      name: maskPersonName(data.personal?.name || '')
    }
  };
}
